import { CentredFlexRow, FlexRowSpacedCentered, FlexRowSpacedRightAligned } from '@fox/layout/src/flex';
import { type CSSProperties, type PropsWithChildren, useEffect } from 'react';
import styled from 'styled-components';
import { LoadingDotsCenter } from '../animated-foxes/index';
import { Button } from '../button/index';
import { MuiStep, MuiStepLabel, MuiStepper } from '../mui-stepper/index';
import { Paper } from '../paper/index';
import { ButtonsRightProps, WizardOptions, WizardStep, WizardValidation, useWizard } from './hooks';

export const WizardHeader = styled(Paper)<{ padding?: string }>`
	padding: ${props => (props.padding ? props.padding : '1rem 1rem 0.5rem')};
	height: 6rem;
	justify-content: space-between;
	background-color: ${props => props.theme.palette.background['paper-elevation-1']};
	display: flex;
	align-items: center;
`;

export const WizardBody = styled.div`
	height: calc(100% - 6rem);
	width: 100%;
	padding: 0;
	overflow: auto;
`;

export const SmallStepWrapper = ({ children }: PropsWithChildren) => {
	return (
		<Paper
			style={{
				maxWidth: '840px',
				minHeight: '100%',
				padding: '2rem 1.5rem',
				margin: '0 auto'
			}}
			elevation={2}
		>
			<div style={{ maxWidth: '480px', margin: '0 auto' }}>{children}</div>
		</Paper>
	);
};

export const MedStepWrapper = ({ children }: PropsWithChildren) => {
	return (
		<Paper
			style={{
				maxWidth: '840px',
				minHeight: '100%',
				padding: '2rem 7.46rem',
				margin: '0 auto'
			}}
			elevation={2}
		>
			{children}
		</Paper>
	);
};

export const MedLargeStepWrapper = ({ children }: PropsWithChildren) => {
	return (
		<Paper
			style={{
				maxWidth: '1040px',
				minHeight: '100%',
				padding: '2rem 7.46rem',
				margin: '0 auto'
			}}
			elevation={2}
		>
			{children}
		</Paper>
	);
};

// maxWidth restored to previous value, a temporary fix to have separation between med and large wrappers
export const LargeStepWrapper = ({ children, style }: PropsWithChildren<{ style?: CSSProperties }>) => {
	return (
		<Paper
			style={{
				maxWidth: '90%',
				minHeight: '100%',
				padding: '2rem clamp(2rem, 5%, 7.46rem)',
				margin: '0 auto',
				display: 'flex',
				flexDirection: 'column',
				...style
			}}
			elevation={2}
		>
			{children}
		</Paper>
	);
};

export const WizardH1FontStyle = { fontWeight: 400 };

export const WizardNextButton = ({ handleNextStep, stepValidations, step, visible = true }: ButtonsRightProps) => {
	if (!visible) return <></>;
	return (
		<Button
			variant="primary"
			title="Next"
			size="medium"
			style={{ textTransform: 'none', whiteSpace: 'nowrap' }}
			disabled={stepValidations.length > step && stepValidations[step + 1] == WizardValidation.Disabled}
			onClick={() => handleNextStep?.()}
		>
			Next
		</Button>
	);
};

const WizardBackButton = ({ hide, handlePrevStep, title }: { hide: boolean; handlePrevStep: () => void; title: string }) => {
	if (hide) return <div />;
	return (
		<Button
			style={{ whiteSpace: 'nowrap' }}
			title={title}
			variant="secondary"
			size="medium"
			onClick={() => handlePrevStep()}
			startIconName="ArrowBackIosNew"
		>
			{title}
		</Button>
	);
};

export const WizardCancelButton = ({ onCancel, step = 0 }: { onCancel: () => void; step?: number }) => {
	const title = step === 0 ? 'Cancel' : 'Close';
	return (
		<Button variant="secondary" size="medium" onClick={onCancel}>
			{title}
		</Button>
	);
};

export const Wizard = <T extends object = {}>({
	steps,
	hideGap,
	onClose,
	...options
}: { steps: WizardStep<T>[]; hideGap?: boolean; onClose?: () => void } & WizardOptions) => {
	const {
		step,
		CurrentStepComponent,
		handleChangeStep,
		handleNextStep,
		handlePrevStep,
		ButtonsLeft,
		ButtonsRight,
		stepVisited,
		stepValidations,
		data
	} = useWizard<T>(steps, options);
	const allValid = !stepValidations.some(v => v !== WizardValidation.Valid);

	// step 0 is always enabled

	const hasPreviousDisabledStep = stepValidations.some((s, sIdx) => s === WizardValidation.Disabled && sIdx < step);
	const stepEnabled =
		step === 0 ||
		(stepValidations[step] !== WizardValidation.Disabled && stepValidations[step] !== WizardValidation.Suppressed && !hasPreviousDisabledStep);

	useEffect(() => {
		// go to the first step if we are not enabled
		if (!stepEnabled) handleChangeStep(0);
	}, [stepEnabled]);
	const lastNonSupressedStep = steps.findLast(
		(s, idx) => stepValidations[idx] !== WizardValidation.Suppressed && stepValidations[idx] !== WizardValidation.Disabled && idx < step
	);
	return (
		<div style={{ height: '100%', maxHeight: '100%' }}>
			<WizardHeader square variant="outlined">
				<CentredFlexRow>
					<FlexRowSpacedCentered style={{ flex: 1 }}>
						{ButtonsLeft ? (
							<ButtonsLeft handlePrevStep={handlePrevStep} />
						) : (
							<>
								<WizardBackButton
									hide={step === 0}
									handlePrevStep={handlePrevStep}
									// Back to last non supressed step
									title={`Back to ${lastNonSupressedStep?.title}`}
								/>
								{onClose ? <WizardCancelButton step={step} onCancel={onClose} /> : null}
							</>
						)}
					</FlexRowSpacedCentered>
					<MuiStepper alternativeLabel={true} style={{ width: '35rem' }}>
						{steps.map((st, i) => {
							const currentStepValid = stepValidations[i] === WizardValidation.Valid;
							const prevSupressedSteps = steps.reduce((count, cur, index) => {
								const isPrev = index < i;
								const isSupressed = stepValidations[index] === WizardValidation.Suppressed;
								return count + (isPrev && isSupressed ? 1 : 0);
							}, 0);
							const previousStepDisabled = stepValidations.some((s, sIdx) => s === WizardValidation.Disabled && sIdx < i);
							const stepDisabled = stepValidations[i] === WizardValidation.Disabled;
							return (
								<MuiStep
									// Because the index is changed to account for supressed steps, manually identify active step
									active={step === i}
									key={st.title}
									// Disable step if its validation equals disabled OR if any step before this step is disabled
									disabled={stepDisabled || previousStepDisabled}
									hidden={stepValidations[i] === WizardValidation.Suppressed}
									onClick={() => {
										if (stepDisabled || previousStepDisabled) return;
										handleChangeStep(i);
									}}
									completed={i < step && currentStepValid}
									style={{ cursor: 'pointer' }}
									// The index of the steps should count up by 1 for each step
									// So this prevents steps showing as 1-3-4 if s2 is supressed
									index={i - prevSupressedSteps}
								>
									<MuiStepLabel error={i < step && !currentStepValid}>{st.title}</MuiStepLabel>
								</MuiStep>
							);
						})}
					</MuiStepper>
					<FlexRowSpacedRightAligned style={{ flex: 1 }}>
						{ButtonsRight ? (
							<ButtonsRight step={step} handleNextStep={handleNextStep} allStepsValid={allValid} stepValidations={stepValidations} />
						) : (
							<WizardNextButton
								visible={step < steps.length - 1}
								handleNextStep={handleNextStep}
								stepValidations={stepValidations}
								step={step}
							/>
						)}
					</FlexRowSpacedRightAligned>
				</CentredFlexRow>
			</WizardHeader>
			{data ? (
				<WizardBody>
					<CurrentStepComponent currentStep={step} nextStep={handleNextStep} stepVisited={stepVisited} />
				</WizardBody>
			) : (
				<LoadingDotsCenter />
			)}
		</div>
	);
};
